import type { ContactDetails } from '../steps/DetailsStep'
import type { ManualVehicle } from '../steps/VehicleStep'
import type {
  AvailableJobTimeSlotDTO,
  ResolveWidgetVehicleResponse,
  UUID,
  WidgetBookingPayment,
} from '../types/PublicBookingChannelTypes'
import type { CheckoutRoute } from './checkoutRedirect'
import type { PaymentPlan } from './payment'
import type { BasketItem } from './services'
import type { TyreSelection } from './tyres'

/**
 * A booking that exists and is waiting on payment.
 *
 * Retrying payment must reuse it. Creating a fresh booking on every attempt
 * left the first one holding the slot, so the garage saw two bookings and the
 * customer could be told their own time had gone.
 */
export interface HeldBooking {
  bookingId: UUID
  /** What the booking was made for. A retry with a different basket or time needs a new one. */
  fingerprint: string
  /** The last Checkout session opened for it, so a hold that can no longer be paid can be checked. */
  sessionId: string | null
  /** Server-issued reference, when the endpoint sends one. */
  reference: string | null
  /**
   * What the server said it will charge. Optional because a snapshot saved by
   * an earlier bundle has no such field.
   */
  payment?: WidgetBookingPayment | null
}

/**
 * How Checkout was opened for the held booking (ACA-99). A widget inside a
 * frame on another site cannot read Stripe's return parameters off the page
 * around it, so this is what lets it pick the payment up when that page loads
 * again, or keep waiting on a payment going on in another tab.
 */
export interface PendingCheckout {
  route: CheckoutRoute
  checkoutUrl: string
  /**
   * Checkout ends on the hosted booking page (ACA-111), which tells the
   * customer how it went: this page never shows it again.
   */
  endsOnHostedPage?: boolean
}

/**
 * Everything the widget needs to pick up where it left off after Stripe.
 *
 * Checkout is a full page navigation, so all React state is gone when the
 * customer comes back. Without this the confirmation after a successful payment
 * showed no date, no vehicle and no services.
 */
export interface PendingBookingSnapshot {
  version: 1
  /** Epoch milliseconds. */
  savedAt: number
  held: HeldBooking
  basket: BasketItem[]
  selectedJobKeys: string[]
  slot: AvailableJobTimeSlotDTO
  plan: PaymentPlan
  registration: string
  vehicle: ResolveWidgetVehicleResponse | null
  manualVehicle: ManualVehicle | null
  details: ContactDetails
  specialRequests: string
  tyreSelection: TyreSelection | null
  /** Absent until Checkout opens, and in snapshots saved by an earlier bundle. */
  checkout?: PendingCheckout | null
}

/** A snapshot older than this is a different visit, not a payment round trip. */
const SNAPSHOT_TTL_MS = 6 * 60 * 60 * 1000

export function pendingBookingKey(publicKey: string): string {
  return `ac_widget:pendingBooking:${publicKey}`
}

function sessionStorageOrNull(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.sessionStorage
  } catch {
    // Accessing the property itself throws when storage is blocked.
    return null
  }
}

function isSnapshot(value: unknown): value is PendingBookingSnapshot {
  if (!value || typeof value !== 'object') return false

  const candidate = value as Record<string, unknown>
  const held = candidate.held as Record<string, unknown> | null | undefined
  const slot = candidate.slot as Record<string, unknown> | null | undefined

  return (
    candidate.version === 1 &&
    typeof candidate.savedAt === 'number' &&
    held != null &&
    typeof held.bookingId === 'string' &&
    typeof held.fingerprint === 'string' &&
    slot != null &&
    typeof slot.startTime === 'string' &&
    typeof slot.endTime === 'string' &&
    Array.isArray(candidate.basket) &&
    Array.isArray(candidate.selectedJobKeys) &&
    candidate.plan != null &&
    typeof candidate.plan === 'object' &&
    typeof candidate.registration === 'string' &&
    candidate.details != null &&
    typeof candidate.details === 'object'
  )
}

/** Saves the snapshot. Returns false when storage is unavailable; the booking still goes ahead. */
export function savePendingBooking(
  publicKey: string,
  snapshot: PendingBookingSnapshot,
  storage: Storage | null = sessionStorageOrNull()
): boolean {
  if (!storage) return false
  try {
    storage.setItem(pendingBookingKey(publicKey), JSON.stringify(snapshot))
    return true
  } catch {
    return false
  }
}

export function readPendingBooking(
  publicKey: string,
  options: { now?: number; storage?: Storage | null } = {}
): PendingBookingSnapshot | null {
  const storage = options.storage === undefined ? sessionStorageOrNull() : options.storage
  if (!storage) return null

  try {
    const raw = storage.getItem(pendingBookingKey(publicKey))
    if (!raw) return null

    // Older bundles stored a bare booking id here, which is not JSON.
    const parsed: unknown = JSON.parse(raw)
    if (!isSnapshot(parsed)) return null

    const now = options.now ?? Date.now()
    return now - parsed.savedAt > SNAPSHOT_TTL_MS ? null : parsed
  } catch {
    return null
  }
}

/** Records how Checkout was opened on the saved snapshot. False when there is none to update. */
export function markPendingCheckout(
  publicKey: string,
  checkout: PendingCheckout,
  storage: Storage | null = sessionStorageOrNull()
): boolean {
  const snapshot = readPendingBooking(publicKey, { storage })
  if (!snapshot) return false
  return savePendingBooking(publicKey, { ...snapshot, checkout }, storage)
}

export function clearPendingBooking(
  publicKey: string,
  storage: Storage | null = sessionStorageOrNull()
): void {
  if (!storage) return
  try {
    storage.removeItem(pendingBookingKey(publicKey))
  } catch {
    // Nothing more can be done; a stale snapshot expires on its own.
  }
}

/** Identifies what a booking was made for, so a retry knows whether it is the same booking. */
export function bookingFingerprint(input: {
  serviceIds: string[]
  slotStart: string
  vehicleReg: string
  customerEmail: string
  /**
   * The tyres, when any (ACA-98): they are priced lines on the booking, so a
   * held booking for other tyres must not be reused.
   */
  tyres?: string | null
}): string {
  const parts = [
    [...input.serviceIds].sort().join(','),
    input.slotStart.trim(),
    input.vehicleReg.toUpperCase().replace(/\s+/g, ''),
    input.customerEmail.trim().toLowerCase(),
  ]
  if (input.tyres) parts.push(input.tyres)
  return parts.join('|')
}

export function canReuseHeldBooking(
  held: HeldBooking | null,
  fingerprint: string
): held is HeldBooking {
  return held != null && held.fingerprint === fingerprint
}
